<template>
  <Card
    class="admin-sidebar"
    :pt="{
      body: 'flex-1 p-2',
      content: 'flex flex-col gap-4 flex-1 h-full',
    }"
  >
    <template #content>
      <div class="flex items-start gap-2 p-4 pb-0">
        <img
          class="admin-sidebar__logo flex-1 min-w-0"
          alt="logo"
          :src="logoUri"
        />
      </div>
      <SidebarMenu :model="topItems" class="flex-1" />
      <SidebarMenu :model="bottomItems" />
      <AdminInboxDrawer />
    </template>
  </Card>
</template>

<script setup lang="ts">
  import type { MenuItem } from "primevue/menuitem";
  import Card from "primevue/card";
  import AdminInboxDrawer from "./AdminInboxDrawer.vue";
  import SidebarMenu, { type SidebarMenuItem } from "./SidebarMenu.vue";
  import defaultLogoUri from "@/assets/images/logo-dark.webp";

  const auth = useAuth();
  const { getGeneralSettings } = useWebsiteContent();
  const { data: generalSettings, suspense: suspenseGeneralSettings } = getGeneralSettings();

  const logoUri = computed<string>(() => {
    return generalSettings.value?.logoDark || defaultLogoUri;
  });

  const inboxDrawerOpen = useState("adminInboxDrawerOpen", () => false);
  const inboxUnreadQuery = useAdminInboxUnreadCount();
  const inboxUnreadCount = computed(
    () => inboxUnreadQuery.data.value?.count ?? 0
  );

  const topItems: SidebarMenuItem[] = [
    {
      label: "General",
      url: "/admin/",
      icon: "fa-solid fa-gear",
      auth: AuthRequirementBuilder.permissions(["pages-settings.manage"]),
    },
    {
      label: "Homepage",
      url: "/admin/homepage",
      icon: "fa-solid fa-home",
      auth: AuthRequirementBuilder.permissions(
        ["pages-settings.manage"],
        "any"
      ),
    },
    {
      label: "Games",
      url: "/admin/games",
      icon: "fa-solid fa-gamepad",
      auth: AuthRequirementBuilder.permissions(
        ["games.view", "analytics.games.view"],
        "any"
      ),
    },
    {
      label: "Social Media",
      url: "/admin/social-media",
      icon: "fa-solid fa-share",
      auth: AuthRequirementBuilder.permissions(["social-media.manage"]),
    },
    {
      label: "Contact",
      url: "/admin/contact",
      icon: "fa-solid fa-comments",
      auth: AuthRequirementBuilder.permissions(
        ["feedback.manage", "feedback.view", "pages-settings.manage"],
        "any"
      ),
    },
    {
      label: "News",
      url: "/admin/news",
      icon: "fa-solid fa-newspaper",
      auth: AuthRequirementBuilder.permissions(
        ["news.manage", "pages-settings.manage"],
        "any"
      ),
    },
    {
      label: "Careers",
      url: "/admin/careers",
      icon: "fa-solid fa-star",
      auth: AuthRequirementBuilder.permissions(
        [
          "careers.benefits.manage",
          "careers.team.manage",
          "pages-settings.manage",
        ],
        "any"
      ),
    },
    {
      label: "About",
      url: "/admin/about",
      icon: "fa-solid fa-circle-info",
      auth: AuthRequirementBuilder.permissions(["pages-settings.manage"]),
    },
    // {
    //   name: 'variables',
    //   label: 'Global Variables',
    //   to: '/admin/variables',
    //   icon: 'fa-square-root-variable',
    //   minimalRole: 'ADMIN',
    //   allowedRoles: ['EDITOR'],
    // },
    {
      label: "Analytics",
      icon: "fa-solid fa-chart-pie",
      auth: AuthRequirementBuilder.permissions(
        ["analytics.company-games-dashboard.view"],
        "any"
      ),
      items: [
        {
          label: "Company Games",
          url: "/admin/analytics/dashboards/company-games",
          icon: "fa-solid fa-gamepad",
          auth: AuthRequirementBuilder.permissions([
            "analytics.company-games-dashboard.view",
          ]),
        },
        {
          label: "Platform Overview",
          url: "/admin/analytics/dashboards/platform-overview",
          icon: "fa-solid fa-globe",
          auth: AuthRequirementBuilder.permissions(["analytics.games.view"]),
        },
        {
          label: "Genres",
          url: "/admin/analytics/dashboards/genres",
          icon: "fa-solid fa-tags",
          auth: AuthRequirementBuilder.permissions(["analytics.games.view"]),
        },
        {
          label: "Up-And-Coming",
          url: "/admin/analytics/up-and-coming",
          icon: "fa-solid fa-arrow-trend-up",
          auth: AuthRequirementBuilder.permissions(["analytics.games.view"]),
        },
        // {
        //   label: "Create Dashboard",
        //   url: "/admin/analytics/create-dashboard",
        //   icon: "fa-solid fa-square-plus",
        // },
      ],
    },
  ];

  const bottomItems = computed<SidebarMenuItem[]>(() => [
    {
      label: "Inbox",
      icon: "fa-solid fa-inbox",
      auth: AuthRequirementBuilder.authenticated(),
      badge: inboxUnreadCount.value > 0 ? inboxUnreadCount.value : undefined,
      command: () => {
        inboxDrawerOpen.value = true;
      },
    },
    {
      label: "Settings",
      url: "/admin/settings",
      icon: "fa-solid fa-cog",
      auth: AuthRequirementBuilder.permissions(["roblox-settings.manage"]),
    },
    {
      label: "Roles",
      url: "/admin/roles/",
      icon: "fa-solid fa-crown",
      auth: AuthRequirementBuilder.permissions(["roles.manage"]),
    },
    {
      label: "Users",
      url: "/admin/users/",
      icon: "fa-solid fa-user",
      auth: AuthRequirementBuilder.permissions(["users.manage"]),
    },
    {
      label: "Back To Website",
      url: "/",
      icon: "fa-solid fa-globe",
    },
    {
      label: "Log Out",
      icon: "fa-solid fa-right-to-bracket",
      command: () => {
        auth.signOut();
      },
    },
  ]);

  await suspenseGeneralSettings();
</script>

<style lang="scss">
</style>
